import { Box, Flex, Heading } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { MachineFireDetails } from 'components/sections/admin-portal/machines/dialogs/settings/machine-live-fires/machine-fire-details';
import { MachineLiveFiresTable } from 'components/sections/admin-portal/machines/dialogs/settings/machine-live-fires/machine-live-fires-table';
import { useMachineLiveFiresDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineLiveFiresDialogState';
import { MachineSubscriptionButton } from 'components/sections/admin-portal/machines/machine-subscription-button';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { MACHINE_SETTINGS_CONTENT_ID } from 'enums/admin-portal';
import useLatest from 'hooks/useLatestRef';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IFireEvent } from 'lib_ts/interfaces/i-session-event';
import { IMachineDetailsFireMsg } from 'lib_ts/interfaces/machine-msg/i-machine-details';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SessionEventsService } from 'services/session-events.service';
import { WebSocketService } from 'services/web-socket.service';
import { useAuthStore } from 'stores/use-auth-store';

const COMPONENT_NAME = 'MachineLiveFires';

export const MachineLiveFires = () => {
  const wsConnected = useAuthStore((s) => s.wsConnected);
  const machine = useMachinesPortalStore((s) => s.machine);

  const { fireID, setFireID, resetFireID } = useMachineLiveFiresDialogState();

  const [fires, setFires] = useState<IFireEvent[]>([]);

  // ID of fires that were recently added/updated via WS for row highlighting
  const [newFireID, setNewFireID] = useState<string>('');
  const [updatedFireID, setUpdatedFireID] = useState<string>('');

  // show FireDetails when a fire is selected (fire ID stored in URL params via setFireID)
  const [selectedFire, setSelectedFire] = useState<IFireEvent>();
  const latestedSelectedFire = useLatest(selectedFire); // stored in ref to avoid adding to WS useEffect deps

  // used to persist table scroll position when navigating to/from fire details
  const scrollPositionRef = useRef<number>();

  // real-time update for new fires
  useEffect(() => {
    if (!wsConnected) {
      return;
    }

    const upsertFire = async (event: CustomEvent) => {
      const data = event.detail as IMachineDetailsFireMsg;

      // just in case
      if (data.fire.machine !== machine?.machineID) {
        return;
      }

      setFires((prev) => {
        const fireIndex = prev.findIndex((s) => s._id === data.fire._id);

        if (data.operation === 'update' && fireIndex >= 0) {
          const newFires = [...prev];
          newFires[fireIndex] = data.fire;
          return newFires;
        }

        return [data.fire, ...prev];
      });

      if (data.operation === 'create') {
        setNewFireID(data.fire._id);
        setUpdatedFireID('');
        return;
      }

      // handlers below are for 'update' operation
      setUpdatedFireID(data.fire._id);
      setNewFireID('');

      // update the fire shown in MachineFireDetails if it's the one currently selected
      if (data.fire._id === latestedSelectedFire.current?._id) {
        setSelectedFire(data.fire);
      }
    };

    const unsubscribe = () => {
      WebSocketService.send(
        WsMsgType.U2S_MachineLiveFires,
        { enable: false, machineID: machine?.machineID },
        COMPONENT_NAME
      );
      WebSocketHelper.remove(WsMsgType.S2U_MachineLiveFires, upsertFire);
    };

    WebSocketService.send(
      WsMsgType.U2S_MachineLiveFires,
      { enable: true, machineID: machine?.machineID },
      COMPONENT_NAME
    );

    WebSocketHelper.on(WsMsgType.S2U_MachineLiveFires, upsertFire);

    return unsubscribe;
  }, [machine?.machineID, wsConnected]);

  const onSelect = (f?: IFireEvent) => {
    if (!f) {
      return;
    }

    // save our scroll position so we can restore it later when the user goes back to the table
    const contentEl = document.getElementById(MACHINE_SETTINGS_CONTENT_ID);
    scrollPositionRef.current = contentEl?.scrollTop;

    setSelectedFire(f);

    // sync up URL param with selected fire
    setFireID(f._id);
  };

  const onBack = () => {
    setSelectedFire(undefined);

    // remove fireID from URL params
    resetFireID();
  };

  // primarily used when the user is visiting a direct link to an fire from outside the app
  useEffect(() => {
    // if we already have the fire selected, no need to fetch it again
    if (fireID === selectedFire?._id) {
      return;
    }

    if (!fireID) {
      setSelectedFire(undefined);
      return;
    }

    const fire = fires.find((s) => s._id === fireID);

    if (fire) {
      setSelectedFire(fire);
      return;
    }

    const lazyLoadFire = async () => {
      const result = await SessionEventsService.getFireByID(fireID);

      if (!result) {
        NotifyHelper.warning({
          message_md: `No fire found with ID ${fireID}`,
        });

        resetFireID();
        return;
      }

      setSelectedFire(result);
    };

    // if we don't have the fire in our current data set, fetch it from the server
    lazyLoadFire();
  }, [fireID, selectedFire?._id, fires]);

  // fires are ordered newest-first, so an older fire ("previous") sits at a
  // higher index and a newer fire ("next") sits at a lower index
  const selectedFireHasPrev = useMemo(() => {
    if (!selectedFire) {
      return false;
    }

    const currentIndex = fires.findIndex((s) => s._id === selectedFire._id);

    if (currentIndex < 0) {
      return false;
    }

    return currentIndex < fires.length - 1;
  }, [selectedFire, fires]);

  const selectedFireHasNext = useMemo(() => {
    if (!selectedFire) {
      return false;
    }

    const currentIndex = fires.findIndex((s) => s._id === selectedFire._id);

    if (currentIndex < 0) {
      return false;
    }

    return currentIndex > 0;
  }, [selectedFire, fires]);

  const onNextFire = () => {
    if (!selectedFire) {
      return;
    }

    const currentIndex = fires.findIndex((s) => s._id === selectedFire._id);
    const nextIndex = currentIndex - 1;

    if (nextIndex >= 0) {
      const nextFire = fires[nextIndex];
      setSelectedFire(nextFire);
      setFireID(nextFire._id);
    }
  };

  const onPrevFire = () => {
    if (!selectedFire) {
      return;
    }

    const currentIndex = fires.findIndex((s) => s._id === selectedFire._id);
    const prevIndex = currentIndex + 1;

    if (prevIndex < fires.length) {
      const prevFire = fires[prevIndex];
      setSelectedFire(prevFire);
      setFireID(prevFire._id);
    }
  };

  return (
    <ErrorBoundary identifier="MachineLiveFires">
      <Flex className={RADIX.VFLEX.GROW} direction="column">
        {selectedFire && (
          <Box>
            <MachineFireDetails
              fire={selectedFire}
              onBack={onBack}
              onPrevFire={onPrevFire}
              onNextFire={onNextFire}
              hasPrev={selectedFireHasPrev}
              hasNext={selectedFireHasNext}
            />
          </Box>
        )}
        <Flex
          className={StringHelper.classNames([
            RADIX.VFLEX.GROW,
            /* always keep table in the DOM to persist input states */
            selectedFire ? 'visibility-hidden' : '',
          ])}
          direction="column"
          gap={RADIX.FLEX.GAP.SM}
        >
          <Flex justify="between" align="center">
            <Heading size="4">Live Fires</Heading>
            <MachineSubscriptionButton type="connection" />
          </Flex>
          <MachineLiveFiresTable
            fires={fires}
            newFireID={newFireID}
            updatedFireID={updatedFireID}
            onSelect={onSelect}
          />
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};
